import React, { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Cone, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { GsUserMenu } from './GsUserMenu';
import type { GsUserMenuLabels } from './GsUserMenu';
import { GsContextSwitcher } from './GsContextSwitcher';
import type { GsContextSwitcherProps } from './GsContextSwitcher';
import { useBranding } from '../branding/BrandingProvider';

export type { GsContextSwitcherProps };

export interface SidebarItem {
  key: string;
  label: string;
  icon: LucideIcon;
  path: string;
  adminOnly?: boolean;
  badge?: string | number;
  externalUrl?: boolean;
}

export interface SidebarBottomUser {
  name: string;
  email?: string;
  role: string;
  initials: string;
}

/** data-tour pour tours guidés (ex. driver.js) — clés par chemin d’app */
const SIDEBAR_TOUR_ATTR: Record<string, string> = {
  '/': 'nav-dashboard',
  '/rapports': 'nav-rapports',
  '/vue-globale': 'nav-overview',
  '/apercu': 'nav-overview',
  '/recherche': 'nav-recherche',
  '/rappels': 'nav-rappels',
  '/finances': 'nav-finances',
  '/financier': 'nav-finances',
  '/materiel': 'nav-materiel',
  '/articles': 'nav-materiel',
  '/utilisateurs': 'nav-utilisateurs',
  '/parametres': 'nav-parametres',
};

export interface GsSidebarProps {
  appName: string;
  items: SidebarItem[];
  adminItems?: SidebarItem[];
  /**
   * Sélecteur de contexte corporate (A3) — intégré au bloc utilisateur en bas
   * de sidebar si `bottomUser` est aussi fourni ; sinon pied standalone.
   * Signature inchangée pour les apps.
   */
  contextSwitcher?: GsContextSwitcherProps;
  /**
   * Zone SUPPORT (UI_STANDARDS §4c « Architecture de navigation — 3 zones ») :
   * modules transverses (feedback, aide, quoi-de-neuf…) rendus EN BAS de la
   * sidebar, juste au-dessus du bloc utilisateur, derrière un divider hairline,
   * styling plus discret que la nav métier. Jamais plus de 3 items. Optionnelle
   * et rétro-compatible : absente = aucun changement de rendu.
   */
  supportItems?: SidebarItem[];
  /**
   * Zone OUTILS (opt-in, rétro-compatible) : modules transverses secondaires
   * (ex. gabarits Console UG) rendus sous la nav métier, avant supportItems.
   */
  toolItems?: SidebarItem[];
  /** Libellé de section pour toolItems (ex. « Outils »). Ignoré si toolItems absent. */
  toolSectionLabel?: string;
  /**
   * Zone CE / exécutif (opt-in, rétro-compatible) : modules réservés à la
   * direction (ex. grille salariale CE) rendus après adminItems, avant
   * supportItems. Absente ou vide = aucun changement de rendu.
   */
  executiveItems?: SidebarItem[];
  /** Libellé de section pour executiveItems (ex. « CE »). Ignoré si executiveItems absent. */
  executiveSectionLabel?: string;
  isAdmin?: boolean;
  activePath?: string;
  onNavigate?: (path: string) => void;
  /** Relancer un tour guidé (ex. depuis le menu utilisateur) */
  onStartTour?: () => void;
  LinkComponent?: React.ComponentType<{ to: string; className?: string; children: React.ReactNode }>;
  bottomUser?: SidebarBottomUser;
  variant?: 'navy' | 'light';
  headerLogoSrc?: string;
  /** Logo alternatif en mode sombre (ex. symbole blanc). Si absent : `headerLogoSrc`. */
  headerLogoSrcDark?: string;
  /**
   * Destination du clic sur le bloc logo + nom d'app (CHORE-UI-LOGO-HOME,
   * standard industrie NN/g : « le logo ramène à l'accueil »). Défaut : '/'.
   * Le clic passe par `LinkComponent` si fourni, sinon `onNavigate` — sans
   * mécanisme de navigation, le bloc reste statique (comportement historique).
   */
  homePath?: string;
  /** Libellé i18n a11y du lien logo (défaut FR : « Retour à l'accueil »). */
  homeLabel?: string;
  mainSectionLabel?: string;
  adminSectionLabel?: string;
  isDark?: boolean;
  onToggleDark?: () => void;
  onLogout?: () => void;
  appVersion?: string;
  /** Clic sur l’avatar du menu bas — ex. panneau profil */
  onUserAvatarClick?: () => void;
  /** Clé localStorage pour persister l'état collapsed (défaut: 'gs_sidebar_collapsed') */
  storageKey?: string;
  /** Libellé i18n du bouton rétracter (défaut FR : « Réduire la sidebar »). */
  collapseLabel?: string;
  /** Libellé i18n du bouton déployer (défaut FR : « Déployer la sidebar »). */
  expandLabel?: string;
  /** Libellés i18n du menu utilisateur (forward à GsUserMenu, défauts FR). */
  userMenuLabels?: GsUserMenuLabels;
}

/** Tooltip minimal affiché à droite d'un item quand la sidebar est rétractée */
function ItemTooltip({ label }: { label: string }) {
  return (
    <div
      className={[
        'pointer-events-none absolute left-full ml-2 z-50',
        'whitespace-nowrap rounded-md bg-slate-800 px-2.5 py-1.5',
        'text-meta font-medium text-white shadow-lg',
        'opacity-0 group-hover:opacity-100 transition-opacity duration-150',
      ].join(' ')}
    >
      {label}
      {/* Flèche gauche */}
      <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
    </div>
  );
}

function useSidebarCollapsed(storageKey: string) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, String(next)); } catch { /* noop */ }
      return next;
    });
  };

  return { collapsed, toggle };
}

export function GsSidebar({
  appName,
  items,
  adminItems,
  contextSwitcher,
  supportItems,
  toolItems,
  toolSectionLabel,
  executiveItems,
  executiveSectionLabel = 'CE',
  isAdmin,
  activePath = '/',
  onNavigate,
  onStartTour,
  LinkComponent,
  bottomUser,
  variant = 'navy',
  headerLogoSrc,
  headerLogoSrcDark,
  homePath = '/',
  homeLabel = 'Retour à l’accueil',
  mainSectionLabel,
  adminSectionLabel = 'Administration',
  isDark = false,
  onToggleDark,
  onLogout,
  appVersion,
  onUserAvatarClick,
  storageKey = 'gs_sidebar_collapsed',
  collapseLabel = 'Réduire la sidebar',
  expandLabel = 'Déployer la sidebar',
  userMenuLabels,
}: GsSidebarProps) {
  const isLight = variant === 'light';
  const { collapsed, toggle } = useSidebarCollapsed(storageKey);

  // Branding runtime (ADR-059) : si un BrandingProvider est monté ET expose des
  // assets, les logos data-driven priment (standard Light_Background en clair /
  // Dark_Background en sombre — JAMAIS de re-teinte CSS d'un logo). Sinon,
  // comportement actuel : props headerLogoSrc / headerLogoSrcDark.
  const branding = useBranding();
  const brandedLogo = isDark
    ? branding?.logos.iconDark ?? branding?.logos.iconLight
    : branding?.logos.iconLight;
  const logoSrc =
    brandedLogo ?? (isDark && headerLogoSrcDark ? headerLogoSrcDark : headerLogoSrc);
  const companyName = branding?.companyName ?? 'Groupe Signalisation';

  // Largeur : 192px déployée, 52px rétractée
  const width = collapsed ? 'w-sidebar-collapsed' : 'w-sidebar-expanded';

  const shell = isLight
    ? 'bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800'
    : 'bg-slate-900';

  const titleClass    = isLight ? 'text-slate-800 dark:text-slate-100' : 'text-white';
  const subtitleClass = isLight ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700';

  const sectionLabelClass = `px-2 mb-1 mt-3 text-chip font-semibold uppercase tracking-[0.12em] ${
    isLight ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700'
  }`;

  const footBorder = isLight
    ? 'border-t border-slate-200 dark:border-slate-800'
    : 'border-t-[0.5px] border-t-[rgba(255,255,255,0.06)]';

  const badgeClass = isLight
    ? 'rounded-full bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-chip font-semibold text-slate-500 dark:text-slate-200 tabular-nums'
    : 'rounded-full bg-white/10 px-1.5 py-0.5 text-chip font-semibold text-white/90 tabular-nums';

  // muted = zone SUPPORT (transverse) : item inactif légèrement plus discret que
  // la nav métier — opacité plutôt qu'une nouvelle couleur (contraste AA bi-mode
  // préservé : hover restaure, l'état ACTIF garde le highlight brand identique).
  const renderItem = (item: SidebarItem, muted = false) => {
    const tourKey = SIDEBAR_TOUR_ATTR[item.path];
    const tourProps = tourKey ? ({ 'data-tour': tourKey } as const) : {};

    const Icon = item.icon;
    const isExternal = item.externalUrl === true;
    const isActive =
      !isExternal &&
      (activePath === item.path || (item.path !== '/' && activePath.startsWith(item.path)));

    const base = collapsed
      ? 'relative group w-full flex items-center justify-center rounded-md py-2 transition-colors border-l-[2px]'
      : 'w-full flex items-center gap-2 rounded-md py-1.5 pl-2 pr-2 text-meta transition-colors border-l-[2px]';

    const activeCls = isActive
      ? 'bg-brand-500/8 text-brand-500 font-medium border-brand-500'
      : isLight
        ? 'text-slate-500 border-transparent hover:bg-slate-50 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800'
        : 'text-slate-500 border-transparent hover:bg-white/5';

    const mutedCls = muted && !isActive ? ' opacity-75 hover:opacity-100' : '';
    const className = `${base} ${activeCls}${mutedCls}`;

    const iconEl = (
      <>
        <Icon size={15} className="shrink-0 opacity-80" />
        {collapsed && <ItemTooltip label={item.label} />}
      </>
    );

    const row = collapsed ? iconEl : (
      <span className="flex w-full min-w-0 items-center justify-between gap-2">
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <Icon size={15} className="shrink-0 opacity-80" />
          <span className="truncate">{item.label}</span>
          {isExternal && <ExternalLink size={11} className="shrink-0 opacity-60" aria-hidden />}
        </span>
        {/* Badge in-place (UI_STANDARDS §4c) : masqué si 0/undefined/'' — un
            compteur à zéro n'est pas une information, c'est du bruit. */}
        {item.badge != null && item.badge !== '' && item.badge !== 0 && (
          <span className={badgeClass}>{item.badge}</span>
        )}
      </span>
    );

    if (isExternal) {
      return (
        <a
          key={item.key}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className={`${className} no-underline`}
          title={collapsed ? item.label : undefined}
          {...tourProps}
        >
          {row}
        </a>
      );
    }
    if (LinkComponent) {
      return (
        <LinkComponent key={item.key} to={item.path} className={className} {...tourProps}>
          {row}
        </LinkComponent>
      );
    }
    return (
      <button
        key={item.key}
        type="button"
        onClick={() => onNavigate?.(item.path)}
        className={className}
        title={collapsed ? item.label : undefined}
        {...tourProps}
      >
        {row}
      </button>
    );
  };

  return (
    <aside
      data-tour="sidebar"
      className={[
        'relative hidden h-full shrink-0 flex-col text-left md:flex',
        'transition-[width] duration-200 ease-in-out',
        width,
        shell,
      ].join(' ')}
    >
      {/* Bouton collapse — flottant sur le bord droit, au niveau du logo */}
      <button
        type="button"
        onClick={toggle}
        title={collapsed ? expandLabel : collapseLabel}
        aria-label={collapsed ? expandLabel : collapseLabel}
        className={[
          'absolute top-5 -right-3 z-50',
          'flex h-6 w-6 items-center justify-center rounded-full',
          'border shadow-md transition-colors',
          isLight
            ? 'bg-white border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-500 dark:hover:text-slate-300'
            : 'bg-slate-800 border-white/10 text-slate-400 hover:text-white',
        ].join(' ')}
      >
        {collapsed
          ? <ChevronRight size={12} strokeWidth={2.5} />
          : <ChevronLeft size={12} strokeWidth={2.5} />}
      </button>

      {/* Logo + nom app — LIEN vers la racine de l'app (CHORE-UI-LOGO-HOME,
          convention universelle NN/g : « logo = accueil », UI_STANDARDS §4c).
          Agnostique routeur : LinkComponent > onNavigate > statique (historique).
          Memes etats hover que les items de nav (aucune nouvelle couleur). */}
      {/* Geometrie inchangee : px-1.5+px-1.5 = px-3 d'origine ; py-2+py-1 = py-3. */}
      <div className="shrink-0 px-1.5 py-2 border-b border-slate-200 dark:border-slate-800">
        {(() => {
          const headerRow = (
            <>
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt=""
                  width={32}
                  height={32}
                  className="size-8 shrink-0 object-contain"
                />
              ) : (
                <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-brand-500" aria-hidden>
                  <Cone className="h-4 w-4 text-white" strokeWidth={2.25} />
                </div>
              )}
              {!collapsed && (
                <div className="min-w-0 flex-1 text-left">
                  <div className={`truncate text-field font-semibold ${titleClass}`}>{appName}</div>
                  <div className={`truncate text-chip ${subtitleClass}`}>{companyName}</div>
                </div>
              )}
            </>
          );
          const headerLayout = `flex w-full items-center rounded-md px-1.5 py-1 ${collapsed ? 'justify-center' : 'gap-2.5'}`;
          const headerInteractive = [
            'cursor-pointer transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60',
            isLight
              ? 'hover:bg-slate-50 dark:hover:bg-slate-800'
              : 'hover:bg-white/5',
          ].join(' ');
          if (LinkComponent) {
            return (
              <LinkComponent to={homePath} className={`${headerLayout} ${headerInteractive} no-underline`}>
                {headerRow}
              </LinkComponent>
            );
          }
          if (onNavigate) {
            return (
              <button
                type="button"
                onClick={() => onNavigate(homePath)}
                aria-label={homeLabel}
                title={collapsed ? homeLabel : undefined}
                className={`${headerLayout} ${headerInteractive}`}
              >
                {headerRow}
              </button>
            );
          }
          return <div className={headerLayout}>{headerRow}</div>;
        })()}
      </div>

      {/* Navigation */}
      <nav className="min-h-0 flex-1 overflow-y-auto px-1.5 py-2 space-y-0.5">
        {!collapsed && mainSectionLabel && (
          <div className={sectionLabelClass}>{mainSectionLabel}</div>
        )}
        {/* .map((i) => renderItem(i)) et PAS .map(renderItem) : l'index du map
            serait passé comme 2e argument `muted` (0 = falsy, 1+ = truthy). */}
        <div className="space-y-0.5">{items.filter((i) => !i.adminOnly).map((i) => renderItem(i))}</div>
        {toolItems && toolItems.length > 0 && (
          <>
            {!collapsed && toolSectionLabel ? (
              <div className={sectionLabelClass}>{toolSectionLabel}</div>
            ) : null}
            <div className="space-y-0.5">{toolItems.map((i) => renderItem(i))}</div>
          </>
        )}
        {isAdmin && adminItems && adminItems.length > 0 && (
          <>
            {!collapsed && <div className={sectionLabelClass}>{adminSectionLabel}</div>}
            <div className="space-y-0.5">{adminItems.map((i) => renderItem(i))}</div>
          </>
        )}
        {executiveItems && executiveItems.length > 0 && (
          <>
            {!collapsed && <div className={sectionLabelClass}>{executiveSectionLabel}</div>}
            <div className="space-y-0.5">{executiveItems.map((i) => renderItem(i))}</div>
          </>
        )}
      </nav>

      {/* Zone SUPPORT (UI_STANDARDS §4c Architecture de navigation — 3 zones) :
          transverse en bas, divider hairline, plus discret que la nav métier,
          au-dessus du bloc utilisateur. Rien ne change si supportItems absent. */}
      {supportItems && supportItems.length > 0 && (
        <div className={`shrink-0 px-1.5 py-1.5 space-y-0.5 ${footBorder}`}>
          {supportItems.map((i) => renderItem(i, true))}
        </div>
      )}

      {/* Pied sidebar : menu user (+ contexte corporate intégré si les deux).
          Un seul filet haut ; pas de select natif. */}
      {(contextSwitcher || bottomUser) && (
        <div className={`shrink-0 ${footBorder}`}>
          {bottomUser ? (
            <GsUserMenu
              user={{
                name: bottomUser.name,
                email: bottomUser.email,
                role: bottomUser.role,
                initials: bottomUser.initials,
              }}
              isDark={isDark}
              onToggleDark={onToggleDark}
              onLogout={onLogout}
              onStartTour={onStartTour}
              onAvatarClick={onUserAvatarClick}
              variant={variant}
              appName={appName}
              version={appVersion}
              collapsed={collapsed}
              labels={userMenuLabels}
              contextSwitcher={contextSwitcher}
            />
          ) : (
            contextSwitcher && (
              <GsContextSwitcher
                {...contextSwitcher}
                collapsed={collapsed}
                variant={variant}
              />
            )
          )}
        </div>
      )}
    </aside>
  );
}
